import { initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";

const firebaseApp = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? "configure-me",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "ghenti.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "ghenti",
  appId: import.meta.env.VITE_FIREBASE_APP_ID ?? "configure-me",
});

export const firebaseAuth = getAuth(firebaseApp);
if (import.meta.env.VITE_IDENTITY_PLATFORM_TENANT_ID) {
  firebaseAuth.tenantId = import.meta.env.VITE_IDENTITY_PLATFORM_TENANT_ID;
}
void setPersistence(firebaseAuth, browserLocalPersistence);

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  loginGoogle: () => Promise<void>;
  loginApple: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(firebaseAuth.currentUser);
  const [loading, setLoading] = useState(true);
  useEffect(() => onAuthStateChanged(firebaseAuth, (next) => {
    setUser(next);
    setLoading(false);
  }), []);
  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    loginGoogle: async () => { await signInWithPopup(firebaseAuth, new GoogleAuthProvider()); },
    loginApple: async () => { await signInWithPopup(firebaseAuth, new OAuthProvider("apple.com")); },
    logout: () => signOut(firebaseAuth),
  }), [loading, user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider is required");
  return value;
}
